import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { vendorAPI, uploadFile, openUploadedFile } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { ErrorMessage, getInputClass } from '../components/ErrorMessage';
import {
  validateStep1, validateStep2, validateStep3, validateStep4,
  validateStep5, validateStep6, validateStep7,
  validatePAN, validateGSTIN, validateIFSC, validateAadhaar,
  validatePincode, validatePhone, validateEmail, validateBankAccount,
  validateUdyamNumber, validateDIPPNumber
} from '../utils/validations';
import { 
  Building2, MapPin, User, Wallet, FileText, Tags, FileCheck, 
  ChevronLeft, ChevronRight, Save, Send, Check, Upload, Loader2, AlertCircle, Eye
} from 'lucide-react';

const STEPS = [
  { id: 1, title: 'Basic Details', icon: Building2 },
  { id: 2, title: 'Address', icon: MapPin },
  { id: 3, title: 'Signatory', icon: User },
  { id: 4, title: 'Bank Details', icon: Wallet },
  { id: 5, title: 'Documents', icon: FileText },
  { id: 6, title: 'Capability', icon: Tags },
  { id: 7, title: 'Declaration', icon: FileCheck },
];

const BUSINESS_TYPES = [
  { value: 'proprietorship', label: 'Proprietorship' },
  { value: 'partnership', label: 'Partnership' },
  { value: 'llp', label: 'Limited Liability Partnership (LLP)' },
  { value: 'private_limited', label: 'Private Limited Company' },
  { value: 'public_limited', label: 'Public Limited Company' },
  { value: 'government', label: 'Government Entity' },
  { value: 'others', label: 'Others' },
];

const VENDOR_CATEGORIES = [
  { value: 'goods', label: 'Goods' },
  { value: 'services', label: 'Services' },
  { value: 'works', label: 'Works' },
  { value: 'consultancy', label: 'Consultancy' },
];

const MSME_CATEGORIES = [
  { value: 'mse', label: 'MSE (Micro/Small Enterprise)' },
  { value: 'sc_st_mse', label: 'SC/ST owned MSE' },
  { value: 'women_mse', label: 'Women owned MSE' },
  { value: 'startup', label: 'Startup' },
];

const COUNTRIES = [
  'India', 'United States', 'United Kingdom', 'Germany', 'France', 'Japan', 
  'China', 'South Korea', 'Singapore', 'Australia', 'Canada', 'UAE', 
  'Saudi Arabia', 'Malaysia', 'Thailand', 'Vietnam', 'Indonesia', 'Others'
];

const INDIAN_BANKS = [
  'State Bank of India', 'Punjab National Bank', 'Bank of Baroda', 'Canara Bank',
  'Union Bank of India', 'Bank of India', 'Indian Bank', 'Central Bank of India',
  'Indian Overseas Bank', 'UCO Bank', 'Bank of Maharashtra', 'Punjab & Sind Bank',
  'HDFC Bank', 'ICICI Bank', 'Axis Bank', 'Kotak Mahindra Bank', 'IndusInd Bank',
  'Yes Bank', 'IDBI Bank', 'Federal Bank', 'South Indian Bank', 'Karur Vysya Bank',
  'City Union Bank', 'Tamilnad Mercantile Bank', 'RBL Bank', 'Bandhan Bank',
  'IDFC First Bank', 'AU Small Finance Bank', 'Equitas Small Finance Bank',
  'Ujjivan Small Finance Bank', 'Others'
];

const STATES = [
  'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh',
  'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka',
  'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram',
  'Nagaland', 'Odisha', 'Punjab', 'Rajasthan', 'Sikkim', 'Tamil Nadu',
  'Telangana', 'Tripura', 'Uttar Pradesh', 'Uttarakhand', 'West Bengal',
  'Delhi', 'Jammu & Kashmir', 'Ladakh', 'Puducherry', 'Chandigarh',
];

export default function VendorRegistration() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadingType, setUploadingType] = useState(null);
  const [application, setApplication] = useState(null);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  const [formData, setFormData] = useState({
    basic_info: {
      company_name: '',
      business_type: '',
      pan_number: '',
      gst_number: '',
      incorporation_date: '',
      annual_turnover: '',
      employee_count: '',
      gem_registered: false,
      gem_registration_id: '',
      cppp_registered: false,
      cppp_registration_id: '',
    },
    registered_address: {
      address_line1: '',
      address_line2: '',
      city: '',
      state: '',
      pincode: '',
      country: 'India',
    },
    correspondence_address: {
      address_line1: '',
      address_line2: '',
      city: '',
      state: '',
      pincode: '',
      country: 'India',
    },
    authorized_signatory: {
      name: '',
      designation: '',
      email: '',
      phone: '',
      aadhaar_number: '',
    },
    bank_details: {
      bank_name: '',
      branch_name: '',
      account_number: '',
      ifsc_code: '',
      account_type: 'current',
    },
    compliance_documents: [],
    capability: {
      categories: [],
      products_services: '',
      is_msme: false,
      msme_number: '',
      msme_category: '',
      is_startup: false,
      startup_dpiit_number: '',
      is_oem: false,
      experience_years: '',
      annual_turnover_year1: '',
      annual_turnover_year2: '',
      annual_turnover_year3: '',
    },
    declaration: {
      not_blacklisted: true,  // Default to "No" (not blacklisted)
      blacklisted_details: '',
      no_conflict_of_interest: false,
      information_correct: false,
      accept_terms: false,
      declaration_date: new Date().toISOString().split('T')[0],
    },
  });

  const [sameAsRegistered, setSameAsRegistered] = useState(false);
  const [completedSteps, setCompletedSteps] = useState(new Set());

  useEffect(() => {
    fetchApplication();
  }, []);

  const fetchApplication = async () => {
    try {
      const response = await vendorAPI.getApplication();
      if (response.data) {
        setApplication(response.data);
        // Merge loaded data with initial state to ensure all nested objects exist
        setFormData(prev => ({
          ...prev,
          basic_info: { ...prev.basic_info, ...(response.data.basic_info || {}) },
          registered_address: { ...prev.registered_address, ...(response.data.registered_address || {}) },
          correspondence_address: { ...prev.correspondence_address, ...(response.data.correspondence_address || {}) },
          authorized_signatory: { ...prev.authorized_signatory, ...(response.data.authorized_signatory || {}) },
          bank_details: { ...prev.bank_details, ...(response.data.bank_details || {}) },
          capability: { ...prev.capability, ...(response.data.capability || {}) },
          declaration: { ...prev.declaration, ...(response.data.declaration || {}) },
          compliance_documents: response.data.compliance_documents || prev.compliance_documents,
        }));
        setCurrentStep(response.data.current_step || 1);
      }
    } catch (error) {
      console.error('Error fetching application:', error);
    }
  };

  const updateFormData = (section, field, value) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value,
      },
    }));
    // Mark as touched
    setTouched(prev => ({
      ...prev,
      [`${section}.${field}`]: true,
    }));
    // Clear error for this field when user types
    setErrors(prev => {
      const newErrors = { ...prev };
      if (newErrors[section]) {
        delete newErrors[section][field];
      }
      return newErrors;
    });
  };

  // Real-time validation handlers
  const handlePANBlur = () => {
    const result = validatePAN(formData.basic_info.pan_number);
    if (!result.valid) {
      setErrors(prev => ({ ...prev, basic_info: { ...prev.basic_info, pan_number: result.error } }));
    }
  };

  const handleGSTINBlur = () => {
    const pan = formData.basic_info.pan_number;
    const gst = formData.basic_info.gst_number;
    const result = validateGSTIN(gst, pan);
    if (!result.valid) {
      setErrors(prev => ({ ...prev, basic_info: { ...prev.basic_info, gst_number: result.error } }));
    } else {
      setErrors(prev => {
        const basic = { ...(prev.basic_info || {}) };
        delete basic.gst_number;
        return { ...prev, basic_info: basic };
      });
    }
  };

  const fillGstinFromPan = () => {
    const panResult = validatePAN(formData.basic_info.pan_number);
    if (!panResult.valid) {
      setErrors(prev => ({
        ...prev,
        basic_info: { ...prev.basic_info, pan_number: panResult.error || 'Enter a valid PAN first' },
      }));
      return;
    }
    const pan = formData.basic_info.pan_number.toUpperCase().trim();
    // Format-valid test GSTIN: state 22 + PAN + entity 1 + Z + check 5
    const gstin = `22${pan}1Z5`;
    updateFormData('basic_info', 'gst_number', gstin);
  };

  const handleIFSCBlur = () => {
    const result = validateIFSC(formData.bank_details.ifsc_code);
    if (!result.valid) {
      setErrors(prev => ({ ...prev, bank_details: { ...prev.bank_details, ifsc_code: result.error } }));
    }
  };

  const handlePhoneBlur = () => {
    const result = validatePhone(formData.authorized_signatory.phone);
    if (!result.valid) {
      setErrors(prev => ({ ...prev, authorized_signatory: { ...prev.authorized_signatory, phone: result.error } }));
    }
  };

  const handleEmailBlur = () => {
    const result = validateEmail(formData.authorized_signatory.email);
    if (!result.valid) {
      setErrors(prev => ({ ...prev, authorized_signatory: { ...prev.authorized_signatory, email: result.error } }));
    }
  };

  const handleAadhaarBlur = () => {
    const result = validateAadhaar(formData.authorized_signatory.aadhaar_number);
    if (!result.valid) {
      setErrors(prev => ({ ...prev, authorized_signatory: { ...prev.authorized_signatory, aadhaar_number: result.error } }));
    }
  };

  const handlePincodeBlur = (section) => {
    const pincode = section === 'registered_address' 
      ? formData.registered_address.pincode 
      : formData.correspondence_address.pincode;
    const result = validatePincode(pincode);
    if (!result.valid) {
      setErrors(prev => ({ ...prev, [section]: { ...prev[section], pincode: result.error } }));
    }
  };

  const handleAccountBlur = () => {
    const result = validateBankAccount(formData.bank_details.account_number);
    if (!result.valid) {
      setErrors(prev => ({ ...prev, bank_details: { ...prev.bank_details, account_number: result.error } }));
    }
  };

  const handleMSMEBlur = () => {
    if (formData.capability.is_msme && formData.capability.msme_number) {
      const result = validateUdyamNumber(formData.capability.msme_number);
      if (!result.valid) {
        setErrors(prev => ({ ...prev, capability: { ...prev.capability, msme_number: result.error } }));
      }
    }
  };

  const handleDPIITBlur = () => {
    if (formData.capability.is_startup && formData.capability.startup_dpiit_number) {
      const result = validateDIPPNumber(formData.capability.startup_dpiit_number);
      if (!result.valid) {
        setErrors(prev => ({ ...prev, capability: { ...prev.capability, startup_dpiit_number: result.error } }));
      }
    }
  };

  const handleSameAsRegistered = (checked) => {
    setSameAsRegistered(checked);
    if (checked) {
      setFormData(prev => ({
        ...prev,
        correspondence_address: { ...prev.registered_address },
      }));
    }
  };

  const handleCategoryChange = (category, checked) => {
    setFormData(prev => ({
      ...prev,
      capability: {
        ...prev.capability,
        categories: checked
          ? [...prev.capability.categories, category]
          : prev.capability.categories.filter(c => c !== category),
      },
    }));
    // Clear category error
    setErrors(prev => {
      const newErrors = { ...prev };
      if (newErrors.capability) delete newErrors.capability.categories;
      return newErrors;
    });
  };

  const handleFileUpload = async (e, documentType) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5MB');
      return;
    }

    setUploadingType(documentType);
    try {
      const result = await uploadFile(file, documentType);
      setFormData(prev => ({
        ...prev,
        compliance_documents: [
          ...prev.compliance_documents.filter(d => d.document_type !== documentType),
          {
            document_type: documentType,
            document_number: '',
            file_path: result.file_path,
            file_name: result.file_name,
            file_id: result.file_id || null,
            uploaded_at: new Date().toISOString(),
            verified: false,
          },
        ],
      }));
      // Clear document error
      setErrors(prev => {
        const newErrors = { ...prev };
        if (newErrors.documents) delete newErrors.documents[documentType];
        return newErrors;
      });
      toast.success(`${documentType.toUpperCase()} uploaded successfully`);
    } catch (error) {
      toast.error('Upload failed: ' + (error.response?.data?.detail || error.message));
    } finally {
      setUploadingType(null);
    }
  };

  // Validate current step
  const validateCurrentStep = () => {
    let result;
    switch (currentStep) {
      case 1:
        result = validateStep1(formData.basic_info);
        if (!result.valid) setErrors(prev => ({ ...prev, basic_info: result.errors }));
        return result.valid;
      case 2:
        result = validateStep2(formData.registered_address, formData.correspondence_address, sameAsRegistered);
        if (!result.valid) setErrors(prev => ({ ...prev, registered_address: result.errors.registered, correspondence_address: result.errors.correspondence }));
        return result.valid;
      case 3:
        result = validateStep3(formData.authorized_signatory);
        if (!result.valid) setErrors(prev => ({ ...prev, authorized_signatory: result.errors }));
        return result.valid;
      case 4:
        result = validateStep4(formData.bank_details);
        if (!result.valid) setErrors(prev => ({ ...prev, bank_details: result.errors }));
        return result.valid;
      case 5:
        result = validateStep5(formData.compliance_documents);
        if (!result.valid) setErrors(prev => ({ ...prev, documents: result.errors }));
        return result.valid;
      case 6:
        result = validateStep6(formData.capability);
        if (!result.valid) setErrors(prev => ({ ...prev, capability: result.errors }));
        return result.valid;
      case 7:
        result = validateStep7(formData.declaration);
        if (!result.valid) setErrors(prev => ({ ...prev, declaration: result.errors }));
        return result.valid;
      default:
        return true;
    }
  };

  const saveProgress = async () => {
    setSaving(true);
    try {
      await vendorAPI.saveApplication({
        ...formData,
        current_step: currentStep,
      });
      toast.success('Progress saved');
    } catch (error) {
      // Handle different error response formats
      let errorMessage = 'Failed to save';
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (typeof detail === 'string') {
          errorMessage = detail;
        } else if (Array.isArray(detail)) {
          // Pydantic validation errors come as array
          errorMessage = detail.map(e => e.msg || e.message || 'Validation error').join(', ');
        } else {
          errorMessage = error.message || 'Unknown error';
        }
      } else {
        errorMessage = error.message || 'Unknown error';
      }
      toast.error('Failed to save: ' + errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const nextStep = async () => {
    // Validate current step before proceeding
    if (!validateCurrentStep()) {
      toast.error('Please fix the errors before proceeding');
      return;
    }
    
    await saveProgress();
    if (currentStep < STEPS.length) {
      setCompletedSteps(prev => new Set([...prev, currentStep]));
      setCurrentStep(currentStep + 1);
      setErrors({}); // Clear errors for new step
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      setErrors({}); // Clear errors when going back
    }
  };

  const goToStep = (stepNumber) => {
    // Allow going to any previously completed step or the current step
    // But don't allow skipping to future steps
    if (stepNumber <= currentStep || completedSteps.has(stepNumber - 1)) {
      setCurrentStep(stepNumber);
      setErrors({});
    } else {
      toast.error('Please complete the previous steps first');
    }
  };

  const submitApplication = async () => {
    // Validate final step
    if (!validateCurrentStep()) {
      toast.error('Please accept all declarations');
      return;
    }

    setLoading(true);
    try {
      await vendorAPI.saveApplication({
        ...formData,
        current_step: 7,
      });
      await vendorAPI.submitApplication();
      toast.success('Application submitted successfully!');
      navigate('/vendor/dashboard');
    } catch (error) {
      toast.error('Submission failed: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="company_name">Company/Firm Name *</Label>
                <Input
                  id="company_name"
                  value={formData.basic_info.company_name}
                  onChange={(e) => updateFormData('basic_info', 'company_name', e.target.value)}
                  placeholder="Enter company name"
                  className={getInputClass(errors.basic_info?.company_name)}
                  data-testid="company-name-input"
                />
                <ErrorMessage error={errors.basic_info?.company_name} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="business_type">Business Type *</Label>
                <Select
                  value={formData.basic_info.business_type}
                  onValueChange={(value) => updateFormData('basic_info', 'business_type', value)}
                >
                  <SelectTrigger 
                    data-testid="business-type-select"
                    className={getInputClass(errors.basic_info?.business_type)}
                  >
                    <SelectValue placeholder="Select business type" />
                  </SelectTrigger>
                  <SelectContent>
                    {BUSINESS_TYPES.map(type => (
                      <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <ErrorMessage error={errors.basic_info?.business_type} />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="pan_number">PAN Number *</Label>
                <Input
                  id="pan_number"
                  value={formData.basic_info.pan_number}
                  onChange={(e) => updateFormData('basic_info', 'pan_number', e.target.value.toUpperCase())}
                  onBlur={handlePANBlur}
                  placeholder="AAAAA0000A"
                  maxLength={10}
                  className={getInputClass(errors.basic_info?.pan_number)}
                  data-testid="pan-number-input"
                />
                <ErrorMessage error={errors.basic_info?.pan_number} />
                <p className="text-xs text-slate-500">Format: 5 letters + 4 digits + 1 letter</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="gst_number">GST Number *</Label>
                <Input
                  id="gst_number"
                  value={formData.basic_info.gst_number}
                  onChange={(e) => updateFormData('basic_info', 'gst_number', e.target.value.toUpperCase())}
                  onBlur={handleGSTINBlur}
                  placeholder="Enter your 15-digit GSTIN"
                  maxLength={15}
                  className={getInputClass(errors.basic_info?.gst_number)}
                  data-testid="gst-number-input"
                />
                <ErrorMessage error={errors.basic_info?.gst_number} />
                <p className="text-xs text-slate-500">
                  {formData.basic_info.pan_number
                    ? `Characters 3–12 of GSTIN must be "${formData.basic_info.pan_number.toUpperCase()}". Do not use 22AAAAA0000A1Z5.`
                    : 'Enter PAN first, then your real GSTIN from the GST certificate.'}
                </p>
                {formData.basic_info.pan_number?.length === 10 && (
                  <button
                    type="button"
                    onClick={fillGstinFromPan}
                    className="text-xs text-blue-600 hover:underline"
                    data-testid="fill-gstin-from-pan"
                  >
                    Fill GSTIN using this PAN (for testing)
                  </button>
                )}
              </div>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              <div className="space-y-2">
                <Label htmlFor="incorporation_date">Date of Incorporation</Label>
                <Input
                  id="incorporation_date"
                  type="date"
                  value={formData.basic_info.incorporation_date}
                  onChange={(e) => updateFormData('basic_info', 'incorporation_date', e.target.value)}
                  max={new Date().toISOString().split('T')[0]}
                  className={getInputClass(errors.basic_info?.incorporation_date)}
                  data-testid="incorporation-date-input"
                />
                <ErrorMessage error={errors.basic_info?.incorporation_date} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="annual_turnover">Annual Turnover (₹ Lakhs)</Label>
                <Input
                  id="annual_turnover"
                  type="number"
                  min="0"
                  value={formData.basic_info.annual_turnover}
                  onChange={(e) => updateFormData('basic_info', 'annual_turnover', e.target.value)}
                  placeholder="0"
                  className={getInputClass(errors.basic_info?.annual_turnover)}
                  data-testid="annual-turnover-input"
                />
                <ErrorMessage error={errors.basic_info?.annual_turnover} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="employee_count">Employee Count</Label>
                <Input
                  id="employee_count"
                  type="number"
                  min="0"
                  value={formData.basic_info.employee_count}
                  onChange={(e) => updateFormData('basic_info', 'employee_count', e.target.value)}
                  placeholder="0"
                  className={getInputClass(errors.basic_info?.employee_count)}
                  data-testid="employee-count-input"
                />
                <ErrorMessage error={errors.basic_info?.employee_count} />
              </div>
            </div>

            {/* GeM and CPPP Registration */}
            <div className="border-t pt-6 mt-6">
              <h4 className="font-semibold mb-4 text-slate-700">Government Portal Registrations</h4>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="border rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Checkbox
                      id="gem_registered"
                      checked={formData.basic_info.gem_registered}
                      onCheckedChange={(checked) => updateFormData('basic_info', 'gem_registered', checked)}
                      data-testid="gem-registered-checkbox"
                    />
                    <Label htmlFor="gem_registered" className="font-medium">
                      Registered with GeM (Government e-Marketplace)?
                    </Label>
                  </div>
                  {formData.basic_info.gem_registered && (
                    <div className="mt-3">
                      <Label htmlFor="gem_registration_id">GeM Registration ID</Label>
                      <Input
                        id="gem_registration_id"
                        value={formData.basic_info.gem_registration_id}
                        onChange={(e) => updateFormData('basic_info', 'gem_registration_id', e.target.value)}
                        placeholder="Enter GeM Registration ID"
                        className="mt-1"
                        data-testid="gem-registration-id-input"
                      />
                    </div>
                  )}
                </div>

                <div className="border rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Checkbox
                      id="cppp_registered"
                      checked={formData.basic_info.cppp_registered}
                      onCheckedChange={(checked) => updateFormData('basic_info', 'cppp_registered', checked)}
                      data-testid="cppp-registered-checkbox"
                    />
                    <Label htmlFor="cppp_registered" className="font-medium">
                      Registered with CPPP (Central Public Procurement Portal)?
                    </Label>
                  </div>
                  {formData.basic_info.cppp_registered && (
                    <div className="mt-3">
                      <Label htmlFor="cppp_registration_id">CPPP Registration ID</Label>
                      <Input
                        id="cppp_registration_id"
                        value={formData.basic_info.cppp_registration_id}
                        onChange={(e) => updateFormData('basic_info', 'cppp_registration_id', e.target.value)}
                        placeholder="Enter CPPP Registration ID"
                        className="mt-1"
                        data-testid="cppp-registration-id-input"
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-8">
            <div>
              <h4 className="font-semibold mb-4">Registered Address</h4>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2 md:col-span-2">
                  <Label>Address Line 1 *</Label>
                  <Input
                    value={formData.registered_address.address_line1}
                    onChange={(e) => updateFormData('registered_address', 'address_line1', e.target.value)}
                    placeholder="Building/Street"
                    className={getInputClass(errors.registered_address?.address_line1)}
                    data-testid="reg-address-line1-input"
                  />
                  <ErrorMessage error={errors.registered_address?.address_line1} />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label>Address Line 2</Label>
                  <Input
                    value={formData.registered_address.address_line2}
                    onChange={(e) => updateFormData('registered_address', 'address_line2', e.target.value)}
                    placeholder="Area/Locality"
                    data-testid="reg-address-line2-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>City *</Label>
                  <Input
                    value={formData.registered_address.city}
                    onChange={(e) => updateFormData('registered_address', 'city', e.target.value)}
                    placeholder="City"
                    className={getInputClass(errors.registered_address?.city)}
                    data-testid="reg-city-input"
                  />
                  <ErrorMessage error={errors.registered_address?.city} />
                </div>
                <div className="space-y-2">
                  <Label>State *</Label>
                  <Select
                    value={formData.registered_address.state}
                    onValueChange={(value) => updateFormData('registered_address', 'state', value)}
                  >
                    <SelectTrigger 
                      data-testid="reg-state-select"
                      className={getInputClass(errors.registered_address?.state)}
                    >
                      <SelectValue placeholder="Select state" />
                    </SelectTrigger>
                    <SelectContent>
                      {STATES.map(state => (
                        <SelectItem key={state} value={state}>{state}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <ErrorMessage error={errors.registered_address?.state} />
                </div>
                <div className="space-y-2">
                  <Label>PIN Code *</Label>
                  <Input
                    value={formData.registered_address.pincode}
                    onChange={(e) => updateFormData('registered_address', 'pincode', e.target.value.replace(/\D/g, ''))}
                    onBlur={() => handlePincodeBlur('registered_address')}
                    placeholder="000000"
                    maxLength={6}
                    className={getInputClass(errors.registered_address?.pincode)}
                    data-testid="reg-pincode-input"
                  />
                  <ErrorMessage error={errors.registered_address?.pincode} />
                </div>
                <div className="space-y-2">
                  <Label>Country *</Label>
                  <Select
                    value={formData.registered_address.country}
                    onValueChange={(value) => updateFormData('registered_address', 'country', value)}
                  >
                    <SelectTrigger data-testid="reg-country-select">
                      <SelectValue placeholder="Select country" />
                    </SelectTrigger>
                    <SelectContent>
                      {COUNTRIES.map(country => (
                        <SelectItem key={country} value={country}>{country}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            <div>
              <div className="flex items-center gap-2 mb-4">
                <Checkbox
                  id="same-address"
                  checked={sameAsRegistered}
                  onCheckedChange={handleSameAsRegistered}
                  data-testid="same-address-checkbox"
                />
                <Label htmlFor="same-address" className="font-normal">
                  Correspondence address same as registered address
                </Label>
              </div>

              {!sameAsRegistered && (
                <>
                  <h4 className="font-semibold mb-4">Correspondence Address</h4>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2 md:col-span-2">
                      <Label>Address Line 1 *</Label>
                      <Input
                        value={formData.correspondence_address.address_line1}
                        onChange={(e) => updateFormData('correspondence_address', 'address_line1', e.target.value)}
                        placeholder="Building/Street"
                        className={getInputClass(errors.correspondence_address?.address_line1)}
                        data-testid="corr-address-line1-input"
                      />
                      <ErrorMessage error={errors.correspondence_address?.address_line1} />
                    </div>
                    <div className="space-y-2 md:col-span-2">
                      <Label>Address Line 2</Label>
                      <Input
                        value={formData.correspondence_address.address_line2}
                        onChange={(e) => updateFormData('correspondence_address', 'address_line2', e.target.value)}
                        placeholder="Area/Locality"
                        data-testid="corr-address-line2-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>City *</Label>
                      <Input
                        value={formData.correspondence_address.city}
                        onChange={(e) => updateFormData('correspondence_address', 'city', e.target.value)}
                        placeholder="City"
                        className={getInputClass(errors.correspondence_address?.city)}
                        data-testid="corr-city-input"
                      />
                      <ErrorMessage error={errors.correspondence_address?.city} />
                    </div>
                    <div className="space-y-2">
                      <Label>State *</Label>
                      <Select
                        value={formData.correspondence_address.state}
                        onValueChange={(value) => updateFormData('correspondence_address', 'state', value)}
                      >
                        <SelectTrigger 
                          data-testid="corr-state-select"
                          className={getInputClass(errors.correspondence_address?.state)}
                        >
                          <SelectValue placeholder="Select state" />
                        </SelectTrigger>
                        <SelectContent>
                          {STATES.map(state => (
                            <SelectItem key={state} value={state}>{state}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <ErrorMessage error={errors.correspondence_address?.state} />
                    </div>
                    <div className="space-y-2">
                      <Label>PIN Code *</Label>
                      <Input
                        value={formData.correspondence_address.pincode}
                        onChange={(e) => updateFormData('correspondence_address', 'pincode', e.target.value.replace(/\D/g, ''))}
                        onBlur={() => handlePincodeBlur('correspondence_address')}
                        placeholder="000000"
                        maxLength={6}
                        className={getInputClass(errors.correspondence_address?.pincode)}
                        data-testid="corr-pincode-input"
                      />
                      <ErrorMessage error={errors.correspondence_address?.pincode} />
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label>Authorised Person Name *</Label>
                <Input
                  value={formData.authorized_signatory.name}
                  onChange={(e) => updateFormData('authorized_signatory', 'name', e.target.value)}
                  placeholder="Enter authorised person name"
                  className={getInputClass(errors.authorized_signatory?.name)}
                  data-testid="signatory-name-input"
                />
                <ErrorMessage error={errors.authorized_signatory?.name} />
              </div>
              <div className="space-y-2">
                <Label>Designation *</Label>
                <Input
                  value={formData.authorized_signatory.designation}
                  onChange={(e) => updateFormData('authorized_signatory', 'designation', e.target.value)}
                  placeholder="e.g., Director, Proprietor"
                  className={getInputClass(errors.authorized_signatory?.designation)}
                  data-testid="signatory-designation-input"
                />
                <ErrorMessage error={errors.authorized_signatory?.designation} />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label>Email *</Label>
                <Input
                  type="email"
                  value={formData.authorized_signatory.email}
                  onChange={(e) => updateFormData('authorized_signatory', 'email', e.target.value)}
                  onBlur={handleEmailBlur}
                  placeholder="email@company.com"
                  className={getInputClass(errors.authorized_signatory?.email)}
                  data-testid="signatory-email-input"
                />
                <ErrorMessage error={errors.authorized_signatory?.email} />
              </div>
              <div className="space-y-2">
                <Label>Phone *</Label>
                <Input
                  value={formData.authorized_signatory.phone}
                  onChange={(e) => updateFormData('authorized_signatory', 'phone', e.target.value.replace(/\D/g, ''))}
                  onBlur={handlePhoneBlur}
                  placeholder="10-digit Indian mobile number"
                  maxLength={10}
                  className={getInputClass(errors.authorized_signatory?.phone)}
                  data-testid="signatory-phone-input"
                />
                <ErrorMessage error={errors.authorized_signatory?.phone} />
                <p className="text-xs text-slate-500">Indian mobile numbers start with 6, 7, 8, or 9</p>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Aadhaar Number (Optional)</Label>
              <Input
                value={formData.authorized_signatory.aadhaar_number}
                onChange={(e) => updateFormData('authorized_signatory', 'aadhaar_number', e.target.value.replace(/\D/g, ''))}
                onBlur={handleAadhaarBlur}
                placeholder="12-digit Aadhaar number"
                maxLength={12}
                className={getInputClass(errors.authorized_signatory?.aadhaar_number)}
                data-testid="signatory-aadhaar-input"
              />
              <ErrorMessage error={errors.authorized_signatory?.aadhaar_number} />
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label>Bank Name *</Label>
                <Select
                  value={formData.bank_details.bank_name}
                  onValueChange={(value) => updateFormData('bank_details', 'bank_name', value)}
                >
                  <SelectTrigger 
                    data-testid="bank-name-select"
                    className={getInputClass(errors.bank_details?.bank_name)}
                  >
                    <SelectValue placeholder="Select bank" />
                  </SelectTrigger>
                  <SelectContent>
                    {INDIAN_BANKS.map(bank => (
                      <SelectItem key={bank} value={bank}>{bank}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <ErrorMessage error={errors.bank_details?.bank_name} />
              </div>
              <div className="space-y-2">
                <Label>Branch Name *</Label>
                <Input
                  value={formData.bank_details.branch_name}
                  onChange={(e) => updateFormData('bank_details', 'branch_name', e.target.value)}
                  placeholder="Enter branch name"
                  className={getInputClass(errors.bank_details?.branch_name)}
                  data-testid="branch-name-input"
                />
                <ErrorMessage error={errors.bank_details?.branch_name} />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label>Account Number *</Label>
                <Input
                  value={formData.bank_details.account_number}
                  onChange={(e) => updateFormData('bank_details', 'account_number', e.target.value.replace(/\D/g, ''))}
                  onBlur={handleAccountBlur}
                  placeholder="Enter account number"
                  maxLength={18}
                  className={getInputClass(errors.bank_details?.account_number)}
                  data-testid="account-number-input"
                />
                <ErrorMessage error={errors.bank_details?.account_number} />
                <p className="text-xs text-slate-500">9-18 digits</p>
              </div>
              <div className="space-y-2">
                <Label>IFSC Code *</Label>
                <Input
                  value={formData.bank_details.ifsc_code}
                  onChange={(e) => updateFormData('bank_details', 'ifsc_code', e.target.value.toUpperCase())}
                  onBlur={handleIFSCBlur}
                  placeholder="e.g., SBIN0001234"
                  maxLength={11}
                  className={getInputClass(errors.bank_details?.ifsc_code)}
                  data-testid="ifsc-code-input"
                />
                <ErrorMessage error={errors.bank_details?.ifsc_code} />
                <p className="text-xs text-slate-500">An 11-character alphanumeric code</p>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Account Type *</Label>
              <Select
                value={formData.bank_details.account_type}
                onValueChange={(value) => updateFormData('bank_details', 'account_type', value)}
              >
                <SelectTrigger 
                  data-testid="account-type-select"
                  className={getInputClass(errors.bank_details?.account_type)}
                >
                  <SelectValue placeholder="Select account type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="current">Current Account</SelectItem>
                  <SelectItem value="savings">Savings Account</SelectItem>
                </SelectContent>
              </Select>
              <ErrorMessage error={errors.bank_details?.account_type} />
            </div>
          </div>
        );

      case 5:
        return (
          <div className="space-y-6">
            <p className="text-sm text-slate-600">
              Upload required statutory documents. Supported formats: PDF, JPEG, PNG (Max 5MB each)
            </p>

            <div className="space-y-4">
              <h4 className="font-semibold text-slate-700 border-b pb-2">Mandatory Documents</h4>
              {[
                { type: 'pan', label: 'PAN Card', required: true },
                { type: 'gst', label: 'GST Certificate', required: true },
                { type: 'cancelled_cheque', label: 'Cancelled Cheque / Bank Statement', required: true },
              ].map((doc) => {
                const uploadedDoc = formData.compliance_documents.find(d => d.document_type === doc.type);
                return (
                <div key={doc.type} className={`border rounded-lg p-4 ${errors.documents?.[doc.type] ? 'border-red-500 bg-red-50' : 'border-slate-200'}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <Label className="font-medium">
                        {doc.label} {doc.required && <span className="text-red-500">*</span>}
                      </Label>
                      {uploadedDoc && (
                        <p className="text-sm text-emerald-600 flex items-center gap-1 mt-1">
                          <Check className="h-4 w-4" />
                          {uploadedDoc.file_name}
                        </p>
                      )}
                      {errors.documents?.[doc.type] && (
                        <p className="text-sm text-red-600 flex items-center gap-1 mt-1">
                          <AlertCircle className="h-4 w-4" />
                          {errors.documents[doc.type]}
                        </p>
                      )}
                    </div>
                    <div className="flex gap-2">
                      {uploadedDoc?.file_path && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={async () => {
                            try {
                              await openUploadedFile(uploadedDoc.file_path);
                            } catch (err) {
                              toast.error(err.response?.data?.detail || err.message || 'Unable to open document');
                            }
                          }}
                          data-testid={`view-${doc.type}-btn`}
                        >
                          <Eye className="h-4 w-4 mr-1" />
                          View
                        </Button>
                      )}
                      <input
                        type="file"
                        id={`file-${doc.type}`}
                        className="hidden"
                        accept=".pdf,.jpg,.jpeg,.png"
                        onChange={(e) => handleFileUpload(e, doc.type)}
                        data-testid={`upload-${doc.type}-input`}
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => document.getElementById(`file-${doc.type}`).click()}
                        disabled={!!uploadingType}
                        data-testid={`upload-${doc.type}-btn`}
                      >
                        {uploadingType === doc.type ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4 mr-1" />}
                        {uploadedDoc ? 'Replace' : 'Upload'}
                      </Button>
                    </div>
                  </div>
                </div>
              )})}
            </div>

            <div className="space-y-4">
              <h4 className="font-semibold text-slate-700 border-b pb-2">Optional Documents</h4>
              {[
                { type: 'incorporation', label: 'Certificate of Incorporation', required: false },
                { type: 'msme', label: 'MSME Certificate (if applicable)', required: false },
                { type: 'product_brochure', label: 'Product Brochure or Catalogue', required: false },
                { type: 'dpiit_certificate', label: 'DPIIT Certificate (Required for vendors from countries sharing land border with India)', required: false, description: 'Vendors/OEMs from countries sharing a land border with India must register with DPIIT to be eligible for government procurement.' },
                { type: 'itr', label: 'Income Tax Returns (Last 3 years)', required: false },
                { type: 'annual_turnover_docs', label: 'Annual Turnover Certificate issued by a Chartered Accountant (past three Financial Years)', required: false },
                { type: 'iso_certification', label: 'ISO Certifications (if available)', required: false },
              ].map((doc) => {
                const uploadedDoc = formData.compliance_documents.find(d => d.document_type === doc.type);
                return (
                <div key={doc.type} className={`border rounded-lg p-4 ${errors.documents?.[doc.type] ? 'border-red-500 bg-red-50' : 'border-slate-200'}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex-1 mr-4">
                      <Label className="font-medium">
                        {doc.label}
                      </Label>
                      {doc.description && (
                        <p className="text-xs text-slate-500 mt-1">{doc.description}</p>
                      )}
                      {uploadedDoc && (
                        <p className="text-sm text-emerald-600 flex items-center gap-1 mt-1">
                          <Check className="h-4 w-4" />
                          {uploadedDoc.file_name}
                        </p>
                      )}
                    </div>
                    <div className="flex gap-2">
                      {uploadedDoc?.file_path && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={async () => {
                            try {
                              await openUploadedFile(uploadedDoc.file_path);
                            } catch (err) {
                              toast.error(err.response?.data?.detail || err.message || 'Unable to open document');
                            }
                          }}
                          data-testid={`view-${doc.type}-btn`}
                        >
                          <Eye className="h-4 w-4 mr-1" />
                          View
                        </Button>
                      )}
                      <input
                        type="file"
                        id={`file-${doc.type}`}
                        className="hidden"
                        accept=".pdf,.jpg,.jpeg,.png"
                        onChange={(e) => handleFileUpload(e, doc.type)}
                        data-testid={`upload-${doc.type}-input`}
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => document.getElementById(`file-${doc.type}`).click()}
                        disabled={!!uploadingType}
                        data-testid={`upload-${doc.type}-btn`}
                      >
                        {uploadingType === doc.type ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4 mr-1" />}
                        {uploadedDoc ? 'Replace' : 'Upload'}
                      </Button>
                    </div>
                  </div>
                </div>
              )})}
            </div>
          </div>
        );

      case 6:
        return (
          <div className="space-y-6">
            <div>
              <Label className="font-medium mb-3 block">Categories of Supply *</Label>
              {errors.capability?.categories && (
                <p className="text-sm text-red-600 flex items-center gap-1 mb-2">
                  <AlertCircle className="h-4 w-4" />
                  {errors.capability.categories}
                </p>
              )}
              <div className="grid grid-cols-2 gap-4">
                {VENDOR_CATEGORIES.map(cat => (
                  <div key={cat.value} className="flex items-center gap-2">
                    <Checkbox
                      id={`cat-${cat.value}`}
                      checked={formData.capability.categories.includes(cat.value)}
                      onCheckedChange={(checked) => handleCategoryChange(cat.value, checked)}
                      data-testid={`category-${cat.value}-checkbox`}
                    />
                    <Label htmlFor={`cat-${cat.value}`} className="font-normal">{cat.label}</Label>
                  </div>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label>Products/Services Description</Label>
              <Textarea
                value={formData.capability.products_services}
                onChange={(e) => updateFormData('capability', 'products_services', e.target.value)}
                placeholder="Describe your products or services in detail"
                rows={4}
                data-testid="products-services-textarea"
              />
            </div>

            {/* MSME Section */}
            <div className="border rounded-lg p-4">
              <div className="flex items-center gap-2 mb-3">
                <Checkbox
                  id="is_msme"
                  checked={formData.capability.is_msme}
                  onCheckedChange={(checked) => updateFormData('capability', 'is_msme', checked)}
                  data-testid="is-msme-checkbox"
                />
                <Label htmlFor="is_msme" className="font-medium">MSME Registered</Label>
              </div>
              {formData.capability.is_msme && (
                <div className="grid md:grid-cols-2 gap-4 mt-3">
                  <div>
                    <Label className="text-sm">Udyam Registration Number</Label>
                    <Input
                      placeholder="UDYAM-XX-00-0000000"
                      value={formData.capability.msme_number}
                      onChange={(e) => updateFormData('capability', 'msme_number', e.target.value.toUpperCase())}
                      onBlur={handleMSMEBlur}
                      className={`mt-1 ${getInputClass(errors.capability?.msme_number)}`}
                      data-testid="msme-number-input"
                    />
                    <ErrorMessage error={errors.capability?.msme_number} />
                  </div>
                  <div>
                    <Label className="text-sm">Category as per MSME Act</Label>
                    <Select
                      value={formData.capability.msme_category}
                      onValueChange={(value) => updateFormData('capability', 'msme_category', value)}
                    >
                      <SelectTrigger className="mt-1" data-testid="msme-category-select">
                        <SelectValue placeholder="Select MSME Category" />
                      </SelectTrigger>
                      <SelectContent>
                        {MSME_CATEGORIES.map(cat => (
                          <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {/* DPIIT Startup Section */}
              <div className={`border rounded-lg p-4 ${errors.capability?.startup_dpiit_number ? 'border-red-500' : 'border-slate-200'}`}>
                <div className="flex items-center gap-2 mb-3">
                  <Checkbox
                    id="is_startup"
                    checked={formData.capability.is_startup}
                    onCheckedChange={(checked) => updateFormData('capability', 'is_startup', checked)}
                    data-testid="is-startup-checkbox"
                  />
                  <Label htmlFor="is_startup" className="font-medium">DPIIT Startup</Label>
                </div>
                {formData.capability.is_startup && (
                  <>
                    <Input
                      placeholder="DPIIT Number"
                      value={formData.capability.startup_dpiit_number}
                      onChange={(e) => updateFormData('capability', 'startup_dpiit_number', e.target.value.toUpperCase())}
                      onBlur={handleDPIITBlur}
                      className={getInputClass(errors.capability?.startup_dpiit_number)}
                      data-testid="dpiit-number-input"
                    />
                    <ErrorMessage error={errors.capability?.startup_dpiit_number} />
                  </>
                )}
              </div>

              {/* OEM Section */}
              <div className="border border-slate-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Checkbox
                    id="is_oem"
                    checked={formData.capability.is_oem}
                    onCheckedChange={(checked) => updateFormData('capability', 'is_oem', checked)}
                    data-testid="is-oem-checkbox"
                  />
                  <Label htmlFor="is_oem" className="font-medium">OEM / Manufacturer</Label>
                </div>
              </div>
            </div>

            {/* Annual Turnover for Past 3 Years */}
            <div className="border rounded-lg p-4">
              <Label className="font-medium mb-3 block">Annual Turnover for Past 3 Financial Years (₹ Lakhs)</Label>
              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <Label className="text-sm text-slate-600">Financial Year 1 (Most Recent)</Label>
                  <Input
                    type="number"
                    min="0"
                    value={formData.capability.annual_turnover_year1}
                    onChange={(e) => updateFormData('capability', 'annual_turnover_year1', e.target.value)}
                    placeholder="0"
                    className="mt-1"
                    data-testid="turnover-year1-input"
                  />
                </div>
                <div>
                  <Label className="text-sm text-slate-600">Financial Year 2</Label>
                  <Input
                    type="number"
                    min="0"
                    value={formData.capability.annual_turnover_year2}
                    onChange={(e) => updateFormData('capability', 'annual_turnover_year2', e.target.value)}
                    placeholder="0"
                    className="mt-1"
                    data-testid="turnover-year2-input"
                  />
                </div>
                <div>
                  <Label className="text-sm text-slate-600">Financial Year 3</Label>
                  <Input
                    type="number"
                    min="0"
                    value={formData.capability.annual_turnover_year3}
                    onChange={(e) => updateFormData('capability', 'annual_turnover_year3', e.target.value)}
                    placeholder="0"
                    className="mt-1"
                    data-testid="turnover-year3-input"
                  />
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Years of Experience</Label>
              <Input
                type="number"
                min="0"
                max="999"
                value={formData.capability.experience_years}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val.length <= 3) {
                    updateFormData('capability', 'experience_years', val);
                  }
                }}
                placeholder="0"
                className="w-32"
                data-testid="experience-years-input"
              />
              <p className="text-xs text-slate-500">Maximum 3 digits</p>
            </div>
          </div>
        );

      case 7:
        return (
          <div className="space-y-6">
            {/* Blacklisting Question */}
            <div className="bg-amber-50 rounded-lg p-6 border border-amber-200">
              <h4 className="font-semibold mb-4 text-amber-800">Blacklisting Status</h4>
              <div className="space-y-4">
                <div>
                  <Label className="font-medium">Have you ever been Blacklisted by any organisation? *</Label>
                  <div className="flex gap-4 mt-2">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="blacklisted"
                        checked={!formData.declaration.not_blacklisted}
                        onChange={() => updateFormData('declaration', 'not_blacklisted', false)}
                        className="w-4 h-4"
                      />
                      <span>Yes</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="blacklisted"
                        checked={formData.declaration.not_blacklisted}
                        onChange={() => updateFormData('declaration', 'not_blacklisted', true)}
                        className="w-4 h-4"
                      />
                      <span>No</span>
                    </label>
                  </div>
                </div>
                {!formData.declaration.not_blacklisted && (
                  <div className="mt-3">
                    <Label>Please provide details of blacklisting *</Label>
                    <Textarea
                      value={formData.declaration.blacklisted_details}
                      onChange={(e) => updateFormData('declaration', 'blacklisted_details', e.target.value)}
                      placeholder="Provide details including organisation name, date, reason, and current status"
                      rows={3}
                      className="mt-1"
                      data-testid="blacklisted-details-textarea"
                    />
                  </div>
                )}
              </div>
            </div>

            <div className="bg-slate-50 rounded-lg p-6 border border-slate-200">
              <h4 className="font-semibold mb-4">Declarations</h4>
              <div className="space-y-4">
                <div className={`flex items-start gap-3 ${errors.declaration?.no_conflict_of_interest ? 'text-red-600' : ''}`}>
                  <Checkbox
                    id="no_conflict"
                    checked={formData.declaration.no_conflict_of_interest}
                    onCheckedChange={(checked) => updateFormData('declaration', 'no_conflict_of_interest', checked)}
                    data-testid="no-conflict-checkbox"
                  />
                  <div>
                    <Label htmlFor="no_conflict" className="font-normal text-sm leading-relaxed">
                      I hereby declare that there is no conflict of interest with any official of the procuring entity. *
                    </Label>
                    {errors.declaration?.no_conflict_of_interest && (
                      <p className="text-xs text-red-600 mt-1">{errors.declaration.no_conflict_of_interest}</p>
                    )}
                  </div>
                </div>

                <div className={`flex items-start gap-3 ${errors.declaration?.information_correct ? 'text-red-600' : ''}`}>
                  <Checkbox
                    id="info_correct"
                    checked={formData.declaration.information_correct}
                    onCheckedChange={(checked) => updateFormData('declaration', 'information_correct', checked)}
                    data-testid="info-correct-checkbox"
                  />
                  <div>
                    <Label htmlFor="info_correct" className="font-normal text-sm leading-relaxed">
                      I hereby certify that all the information provided in this application is true, correct, 
                      and complete to the best of my knowledge and belief. *
                    </Label>
                    {errors.declaration?.information_correct && (
                      <p className="text-xs text-red-600 mt-1">{errors.declaration.information_correct}</p>
                    )}
                  </div>
                </div>

                <div className={`flex items-start gap-3 ${errors.declaration?.accept_terms ? 'text-red-600' : ''}`}>
                  <Checkbox
                    id="accept_terms"
                    checked={formData.declaration.accept_terms}
                    onCheckedChange={(checked) => updateFormData('declaration', 'accept_terms', checked)}
                    data-testid="accept-terms-checkbox"
                  />
                  <div>
                    <Label htmlFor="accept_terms" className="font-normal text-sm leading-relaxed">
                      I accept the terms and conditions of vendor registration and agree to comply with all 
                      procurement rules and regulations. *
                    </Label>
                    {errors.declaration?.accept_terms && (
                      <p className="text-xs text-red-600 mt-1">{errors.declaration.accept_terms}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <p className="text-sm text-amber-800">
                <strong>Note:</strong> By submitting this application, you confirm that all documents are 
                authentic and the information provided is accurate. Any false declaration may lead to 
                rejection and/or blacklisting.
              </p>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="tricolor-strip" />
      <header className="bg-white border-b border-slate-200 shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <img 
                src="/iith-logo.png" 
                alt="Emblem of India" 
                className="h-12 w-auto"
              />
              <div>
                <h1 className="text-xl font-bold govt-blue-text font-serif">Vendor Registration</h1>
                <p className="text-sm text-slate-600">Complete your registration form</p>
              </div>
            </div>
            <div className="flex items-center gap-2 text-sm text-slate-600">
              <span>Logged in as:</span>
              <span className="font-medium">{user?.name}</span>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex gap-8">
          {/* Stepper Sidebar */}
          <div className="hidden lg:block w-64 flex-shrink-0">
            <Card className="govt-card sticky top-24">
              <CardHeader className="govt-card-header">
                <CardTitle className="text-sm">Registration Steps</CardTitle>
              </CardHeader>
              <CardContent className="p-4">
                <div className="space-y-2">
                  {STEPS.map((step) => {
                    const isCompleted = completedSteps.has(step.id) || currentStep > step.id;
                    const isCurrent = currentStep === step.id;
                    const isAccessible = step.id <= currentStep || completedSteps.has(step.id - 1);
                    
                    return (
                      <button
                        key={step.id}
                        onClick={() => goToStep(step.id)}
                        disabled={!isAccessible}
                        className={`w-full flex items-center gap-3 px-3 py-2 rounded-md text-left transition-colors ${
                          isCurrent
                            ? 'bg-[#003380] text-white'
                            : isCompleted
                            ? 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100'
                            : isAccessible
                            ? 'hover:bg-slate-100 text-slate-600'
                            : 'text-slate-400 cursor-not-allowed opacity-60'
                        }`}
                        data-testid={`step-${step.id}-btn`}
                      >
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold ${
                          isCurrent
                            ? 'bg-white/20 text-white'
                            : isCompleted
                            ? 'bg-emerald-500 text-white'
                            : 'bg-slate-200 text-slate-500'
                        }`}>
                          {isCompleted && !isCurrent ? <Check className="h-3 w-3" /> : step.id}
                        </div>
                        <span className="text-sm font-medium">{step.title}</span>
                      </button>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Main Content */}
          <div className="flex-1">
            <Card className="govt-card">
              <CardHeader className="govt-card-header">
                <div className="flex items-center gap-3">
                  {(() => {
                    const StepIcon = STEPS[currentStep - 1].icon;
                    return <StepIcon className="h-5 w-5 text-[#003380]" />;
                  })()}
                  <div>
                    <CardTitle>Step {currentStep}: {STEPS[currentStep - 1].title}</CardTitle>
                    <CardDescription>Fill in the required information</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-6">
                {renderStepContent()}

                {/* Navigation: Previous left; Save + Next/Submit grouped right */}
                <div className="flex items-center justify-between gap-3 mt-8 pt-6 border-t border-slate-200">
                  <Button
                    variant="outline"
                    onClick={prevStep}
                    disabled={currentStep === 1}
                    className="govt-btn-secondary"
                    data-testid="prev-step-btn"
                  >
                    <ChevronLeft className="h-4 w-4 mr-2" />
                    Previous
                  </Button>

                  <div className="flex items-center gap-3 flex-wrap justify-end">
                    <Button
                      variant="outline"
                      onClick={saveProgress}
                      disabled={saving}
                      data-testid="save-progress-btn"
                    >
                      {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                      Save Progress
                    </Button>

                    {currentStep < STEPS.length ? (
                      <Button
                        onClick={nextStep}
                        className="govt-btn-primary"
                        data-testid="next-step-btn"
                      >
                        Next
                        <ChevronRight className="h-4 w-4 ml-2" />
                      </Button>
                    ) : (
                      <Button
                        onClick={submitApplication}
                        disabled={loading}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white"
                        data-testid="submit-application-btn"
                      >
                        {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
                        Submit Application
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
